import React, { useState } from 'react';
import FileSaver from 'file-saver';
import autoTable from 'jspdf-autotable';
import axios from 'axios';
import { Workbook  } from 'exceljs';
import { jsPDF } from 'jspdf';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { sanitizeFilename } from '../../scripts/document.filename.script';
import { GlobalSwal } from '../../styles/swal.style';
import { Company } from '../../models/company.model';
import { Contact } from '../../models/contact.model';
import { legendInvalid, legendValidInvalidRestart } from '../../utils/legend.data.util';
import { modalHide } from '../../utils/modal.util';
import { contactSelected } from '../../scripts/list.selected.script';

import AppInputExportType from '../element/InputExportType';
import logotipo from '../../assets/themes/svg/logo-official.png';

export interface AppModalContactEntryExportProps {
  companyForUser: Company | null,
  contactsForCompany: Contact[] | null,
  table: {body: any[], foot: any[]},
  contact: string | undefined | null
};

const AppModalContactEntryExport: React.FunctionComponent<AppModalContactEntryExportProps> = ({companyForUser, contactsForCompany, table, contact}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [formatExport, setFormatExport] = useState({value: '', valid: false})

  const fileExcel = async (company: Company, contacts: Contact[], tableTitle: string[][], tableData: any[][], id: string) => {
    let workbook = new Workbook()
    let worksheet = workbook.addWorksheet('Contacts Data')

    worksheet.addRow([])
    worksheet.mergeCells(`A1:E1`)
    worksheet.getRow(1).height = 35

    let imageBuffer = await axios.get(logotipo, {responseType: 'arraybuffer'})

    let imageLogotipo = workbook.addImage({
      buffer: imageBuffer.data,
      extension: 'png'
    })

    worksheet.addImage(imageLogotipo, { tl: {col: 0.1, row: 0.2}, ext: {width: 100, height: 30}})

    let titleRow = worksheet.addRow([`${company.name} - ${lang.labels.accountStatement}`])
    titleRow.font = {size: 17, bold: true}
    titleRow.getCell(1).alignment = {vertical: 'distributed', horizontal: 'center'}

    worksheet.mergeCells(`A${titleRow.number}:E${titleRow.number}`)
    worksheet.getRow(2).height = 30

    let subtitleRow = worksheet.addRow([contactSelected(contacts, id)])
    subtitleRow.font = {size: 15, bold: true}
    subtitleRow.getCell(1).alignment = {vertical: 'distributed', horizontal: 'center'}

    worksheet.mergeCells(`A${subtitleRow.number}:E${subtitleRow.number}`)
    worksheet.getRow(3).height = 20

    worksheet.addRow([])
    worksheet.mergeCells(`A4:E4`)
    worksheet.getRow(4).height = 10

    let headerRow = worksheet.addRow(tableTitle[0])
    headerRow.font = {bold: true, color: {argb: 'FFFFFF'}}
    headerRow.eachCell( (cell) => {
      cell.alignment = {vertical: 'distributed', horizontal: 'center'}
      cell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: '2980BA'}, bgColor: {argb: '2980BA'}}
    })

    worksheet.getRow(5).height = 20

    tableData.forEach((item) => {
      let row = worksheet.addRow(item)
      row.getCell(3).alignment = { horizontal: 'right' }
      row.getCell(4).alignment = { horizontal: 'right' }
      row.getCell(5).alignment = { horizontal: 'right' }
    })

    worksheet.getColumn(1).width = 20;
    worksheet.getColumn(2).width = 50;
    worksheet.getColumn(3).width = 25;
    worksheet.getColumn(4).width = 25;
    worksheet.getColumn(5).width = 25;

    let lastRowIndex = worksheet.rowCount
    let footer = [worksheet.getRow(lastRowIndex - 1), worksheet.getRow(lastRowIndex)]

    footer.forEach((row) => {
      row.font = { size: 10, bold: true }
    })

    workbook.xlsx.writeBuffer().then( data => {
      let blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      FileSaver.saveAs(blob, `Statements_of_${contactSelected(contacts, id).replaceAll(' ', '_')}_for_${company.name.replaceAll(' ', '_')}.xlsx`);
    })
  }

  const filePdf = async (company: Company, contacts: Contact[], tableTitle: string[][], tableData: any[][], id: string) => {
    let document = new jsPDF('landscape', 'mm', [340, 220])

    document.addImage(logotipo, 'PNG', 290, 5, 30, 9)

    document.setFontSize(17)
    document.setFont('helvetica', 'bold')
    document.text(`${company.name} - ${lang.labels.accountStatement}`, 25, 20)

    document.setFontSize(15)
    document.setFont('helvetica', 'bold')
    document.text(contactSelected(contacts, id), 25, 28)

    autoTable(document, {
      head: tableTitle,
      body: tableData,
      startY: 33,
      headStyles: { halign: 'center' },
      bodyStyles: { fontSize: 10 },
      columnStyles: {
        [tableTitle[0].length - 3]: { halign: 'right' },
        [tableTitle[0].length - 2]: { halign: 'right' },
        [tableTitle[0].length - 1]: { halign: 'right' }
      },
      didParseCell: (data) => {
        let lastRowIndex = tableData.length - 1
        let secondLastRowIndex = tableData.length - 2

        if (data.row.index === lastRowIndex || data.row.index === secondLastRowIndex) {
          data.cell.styles.fontStyle = 'bold'
          data.cell.styles.fontSize = 9
        }
      }
    })

    document.save(`Statements_of_${sanitizeFilename(contactSelected(contacts, id)).replaceAll(' ', '_')}_for_${sanitizeFilename(company.name).replaceAll(' ', '_')}.pdf`)
  }

  const executeContactExport = () => {
    setLoadIndicator('on')

    if (companyForUser && contactsForCompany && table && contact && formatExport.valid) {
      let tableTitle: string[][] = [[lang.labels.date.toUpperCase(), lang.labels.description.toUpperCase(), `${lang.labels.debit.toUpperCase()} (${companyForUser.currency?.symbol})`, `${lang.labels.credit.toUpperCase()} (${companyForUser.currency?.symbol})`, `${lang.labels.balance.toUpperCase()} (${companyForUser.currency?.symbol})`]]
      let tableData: any[][]  = [[]]

      tableData.pop()

      for (let detail of table.body) {
        tableData.push([detail.date, detail.description, detail.debit, detail.credit, detail.balance])
      }

      for (let detail of table.foot) {
        tableData.push([detail.date, detail.description, detail.debit, detail.credit, detail.balance])
      }

      setTimeout( () => {
        switch (formatExport.value) {
          case 'excel':
            fileExcel(companyForUser, contactsForCompany, tableTitle, tableData, contact)
            break;
          case  'pdf':
            filePdf(companyForUser, contactsForCompany, tableTitle, tableData, contact)
            break;
        }

        setLoadIndicator('off')
        executeHideModalContactExport()
      }, 1000)
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        setLoadIndicator('off')
        legendInvalid('modal-contact-export-accounting-entry-legend-format-export-type-required')
      })
    }
  }

  const executeHideModalContactExport = () => {
    modalHide('modal-contact-export-accounting-entry')

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function restartModal() {
    legendValidInvalidRestart('modal-contact-export-accounting-entry-legend-format-export-type-required')
    setFormatExport({value: '', valid: false})
  }

  return (
    <div id="modal-contact-export-accounting-entry" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-md">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.exportEntryContacts}</h2>
          </div>
          <div className="modal-body">
            <div className="form">
              <div className="fv-row">
                <label className="form-label required">{lang.labels.selectExportFormat}:</label>
                <AppInputExportType component={"modal-contact-export-accounting-entry"} formatExport={formatExport} setFormatExport={setFormatExport}></AppInputExportType>
              </div>
            </div>
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModalContactExport}>{lang.labels.discard}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={executeContactExport}>
              <span className="indicator-label">{lang.labels.export}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AppModalContactEntryExport;
